import { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { TrendingUp, TrendingDown, Minus, Zap, Award, Calendar, Share2 } from 'lucide-react'
import StrategyLevelBadge from '../StrategyLevelBadge'
import StrategyProgressShareModal from '../StrategyProgressShareModal'
import strategyLevelService, { MAX_STRATEGY_LEVEL } from '../../services/strategyLevelService'

/**
 * Compact, scannable table of every strategy the user can practise — one row
 * per strategy. Replaces the old grid of mismatched cards on the dashboard.
 * The full per-strategy detail card still lives on the /strategy/:type page.
 */

const formatRelativeDate = (date) => {
  if (!date) return 'Never'
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return 'Never'
  // Compare on local calendar-day boundaries (not elapsed UTC ms) so the
  // Today/Yesterday labels match the user's wall clock regardless of timezone.
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const days = Math.round((startOfToday.getTime() - startOfDate.getTime()) / 86400000)
  if (days <= 0) return 'Today' // includes future timestamps from clock skew
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  return d.toLocaleDateString()
}

const MiniPill = ({ icon: Icon, children, tone }) => {
  const tones = {
    green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
  }
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tones[tone] || tones.gray}`}
    >
      {Icon && <Icon className="w-2.5 h-2.5" />}
      {children}
    </span>
  )
}

MiniPill.propTypes = {
  icon: PropTypes.elementType,
  children: PropTypes.node,
  tone: PropTypes.oneOf(['green', 'blue', 'gray'])
}

const TH =
  'px-3 py-2.5 font-semibold text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500'

const StrategyLevelTable = ({
  strategies = [],
  strategyLevels = [],
  currentChallenge = null,
  onStart,
  onContinue,
  onView,
  publicName = null
}) => {
  const [shareStrategy, setShareStrategy] = useState(null)

  const rows = useMemo(
    () =>
      strategies
        .map((s) => {
          const strategyType = s.strategyType || s.type
          if (!strategyType) return null
          const level = strategyLevels.find((l) => l.strategyType === strategyType) || null
          return { strategyType, level }
        })
        .filter(Boolean)
        .sort((a, b) => {
          const aStarted = !!a.level
          const bStarted = !!b.level
          if (aStarted !== bStarted) return aStarted ? -1 : 1
          if (aStarted && bStarted) {
            return (
              new Date(b.level.lastChallengeAt || 0).getTime() -
              new Date(a.level.lastChallengeAt || 0).getTime()
            )
          }
          return 0
        }),
    [strategies, strategyLevels]
  )

  return (
    <>
      <div className="overflow-x-auto pb-1">
        <table className="w-full min-w-[720px] text-sm" aria-label="Strategy levels and progress">
          <thead>
            <tr className="text-left border-b border-gray-100 dark:border-gray-700">
              <th scope="col" className={`${TH} pl-4 sm:pl-6`}>
                Strategy
              </th>
              <th scope="col" className={TH}>
                Level
              </th>
              <th scope="col" className={`${TH} text-right`}>
                Avg
              </th>
              <th scope="col" className={`${TH} text-right`}>
                Done
              </th>
              <th scope="col" className={`${TH} text-right`}>
                Best
              </th>
              <th scope="col" className={TH}>
                Mastery
              </th>
              <th scope="col" className={TH}>
                Last activity
              </th>
              <th scope="col" className={`${TH} pr-4 sm:pr-6 text-right`}>
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
            {rows.map(({ strategyType, level }) => {
              const name = strategyLevelService.getStrategyDisplayName(strategyType)
              const icon = strategyLevelService.getStrategyIcon(strategyType)
              const hasActiveChallenge = currentChallenge?.strategyType === strategyType

              if (!level) {
                return (
                  <tr
                    key={strategyType}
                    className="text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/40"
                  >
                    <td className="pl-4 sm:pl-6 pr-3 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 text-lg grayscale opacity-60">
                          {icon}
                        </span>
                        <div>
                          <div className="font-semibold text-gray-700 dark:text-gray-300">
                            {name}
                          </div>
                          <div className="text-[11px]">
                            {hasActiveChallenge ? 'Challenge in progress' : 'Not started yet'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3 text-right">—</td>
                    <td className="px-3 py-3 text-right">—</td>
                    <td className="px-3 py-3 text-right">—</td>
                    <td className="px-3 py-3">
                      <div className="w-20 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800" />
                    </td>
                    <td className="px-3 py-3">Never</td>
                    <td className="pr-4 sm:pr-6 pl-3 py-3 text-right">
                      <button
                        onClick={() =>
                          hasActiveChallenge ? onContinue?.(strategyType) : onStart?.(strategyType)
                        }
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                          hasActiveChallenge
                            ? 'bg-warning-600 text-white hover:bg-warning-700'
                            : 'border border-primary-300 text-primary-700 hover:bg-primary-50 dark:border-primary-700 dark:text-primary-400 dark:hover:bg-primary-900/20'
                        }`}
                      >
                        {hasActiveChallenge ? 'Continue' : 'Start'}
                      </button>
                    </td>
                  </tr>
                )
              }

              const avg = level.averageScore || 0
              const best = level.bestScore || 0
              const completed = level.completedChallenges || 0
              const mastery = level.currentLevel
                ? Math.round((level.currentLevel / MAX_STRATEGY_LEVEL) * 100)
                : 0
              const scoreDiff = best - avg
              const hasHighPotential = scoreDiff > 20
              const isConsistent = scoreDiff < 10 && completed >= 3
              const levelsGained = (level.currentLevel || 0) - (level.startingLevel || 0)
              const hasDropped = (level.currentLevel || 0) < (level.highestLevel || 0)
              const TrendIcon = levelsGained > 0 ? TrendingUp : hasDropped ? TrendingDown : Minus
              const trendColor =
                levelsGained > 0
                  ? 'text-success-600 dark:text-success-400'
                  : hasDropped
                    ? 'text-warning-600 dark:text-warning-400'
                    : 'text-gray-400'

              return (
                <tr key={strategyType} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                  <td className="pl-4 sm:pl-6 pr-3 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-lg">
                        {icon}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {name}
                          </span>
                          {levelsGained > 0 && <MiniPill tone="green">+{levelsGained}</MiniPill>}
                        </div>
                        {(hasHighPotential || isConsistent) && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {hasHighPotential && (
                              <MiniPill icon={Zap} tone="blue">
                                High potential
                              </MiniPill>
                            )}
                            {isConsistent && (
                              <MiniPill icon={Award} tone="gray">
                                Consistent
                              </MiniPill>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <StrategyLevelBadge level={level.currentLevel} size="small" showLabel={false} />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <span className="inline-flex items-center gap-1 font-semibold text-gray-900 dark:text-gray-100">
                      {avg.toFixed(0)}%
                      <TrendIcon className={`w-3.5 h-3.5 ${trendColor}`} />
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right text-gray-700 dark:text-gray-300">
                    {completed}
                  </td>
                  <td className="px-3 py-3 text-right font-semibold text-warning-600 dark:text-warning-400">
                    {best.toFixed(0)}%
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary-500 to-purple-500"
                          style={{ width: `${mastery}%` }}
                        />
                      </div>
                      <span className="text-[11px] text-gray-400">{mastery}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatRelativeDate(level.lastChallengeAt)}
                    </span>
                  </td>
                  <td className="pr-4 sm:pr-6 pl-3 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        onClick={() =>
                          hasActiveChallenge ? onContinue?.(strategyType) : onStart?.(strategyType)
                        }
                        className={`px-2.5 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                          hasActiveChallenge
                            ? 'bg-warning-600 text-white hover:bg-warning-700'
                            : 'bg-primary-600 text-white hover:bg-primary-700'
                        }`}
                      >
                        {hasActiveChallenge ? 'Continue' : 'New'}
                      </button>
                      <button
                        onClick={() => onView?.(strategyType)}
                        className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      >
                        Progress
                      </button>
                      <button
                        onClick={() => setShareStrategy(level)}
                        title={`Share your ${name} progress`}
                        aria-label={`Share your ${name} progress`}
                        className="p-1.5 rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-purple-600 hover:border-purple-300 dark:hover:text-purple-400 dark:hover:border-purple-700 transition-colors"
                      >
                        <Share2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <StrategyProgressShareModal
        isOpen={!!shareStrategy}
        onClose={() => setShareStrategy(null)}
        strategyLevel={shareStrategy}
        publicName={publicName}
      />
    </>
  )
}

StrategyLevelTable.propTypes = {
  strategies: PropTypes.arrayOf(PropTypes.object),
  strategyLevels: PropTypes.arrayOf(PropTypes.object),
  currentChallenge: PropTypes.object,
  onStart: PropTypes.func,
  onContinue: PropTypes.func,
  onView: PropTypes.func,
  publicName: PropTypes.string
}

export default StrategyLevelTable
